/** 
Example:
#/foobar/foo/bar/fizz/buzz/#cbbc/udstiller/svend-brinkman/

Grammar:
<NavigationStateFragments>: <NavigationStateFragment{0,}>
<NavigationStateFragment>: #/<formID>/<ViewState>/
<formID>: <string>
<ViewState>: <DetailViewState>
<DetailViewState>: <string>/<string> // <moduleslug>/<elementSlug>
<string>: [a-z\_,\-0-9]+ // any json value
```
 */

import { Either } from "fp-ts/lib/Either";
import * as C from "parser-ts/lib/char";
import * as S from "parser-ts/lib/string";
import * as P from "parser-ts/lib/Parser";
import { flow, pipe } from "fp-ts/lib/function";
import { skip, UGParser } from "./parserExtensions";
import { either, readonlyArray } from "fp-ts";

// -------------------------------------------------------------------------------------
// constructors
// -------------------------------------------------------------------------------------

export type ViewStateType = "DetailView" | "LocationView";

export type ViewStateDetailView = {
  type: "DetailView";
  moduleSlug: string;
  elementSlug: string;
};
export const ViewStateDetailView = (
  moduleSlug: string,
  elementSlug: string
): ViewStateDetailView => ({ type: "DetailView", moduleSlug, elementSlug });

export type ViewStateLocationView = {
  type: "LocationView";
  locationSlug: string;
};
export const ViewStateLocationView = (
  locationSlug: string
): ViewStateLocationView => ({ type: "LocationView", locationSlug });

export type ViewState = ViewStateDetailView | ViewStateLocationView;

export type NavigationPath = {
  formId: string;
  views: ViewState[];
};
export const NavigationPath = (
  formId: string,
  views: ViewState[]
): NavigationPath => ({ formId, views });

const SeperatorParser = C.char("/");

const IdentifierCharParser = P.either(C.alphanum, () => C.char("-"));
// <string>: [a-zA-Z0-9_]
const IdentifierParser = C.many1(IdentifierCharParser);
// <formID>: <string>
const FormIDParser = P.expected(IdentifierParser, "FormID");

// <ViewStateDetailView>: <string>/<string> // <moduleslug>/<elementSlug>
const DetailViewStateParser: P.Parser<string, ViewState> = pipe(
  P.expected(IdentifierParser, "Module slug"),
  P.bindTo("moduleSlug"),
  skip(() => SeperatorParser),
  P.bind("elementSlug", () => P.expected(IdentifierParser, "Element slug")),
  P.map(({ moduleSlug, elementSlug }) =>
    ViewStateDetailView(moduleSlug, elementSlug)
  )
);

// <LocationViewState>: "location/" <string> // location/<location_slug>
const LocationViewStateParser: P.Parser<string, ViewState> = pipe(
  P.expected(S.string("location"), "'Location'"),
  skip(() => SeperatorParser),
  P.bind("locationSlug", () => P.expected(IdentifierParser, "Location slug")),
  P.map(({ locationSlug }) => ViewStateLocationView(locationSlug))
);

// <ViewState>: <ViewStateDetailView> | <SomeOtherViewState>
const ViewStateParser = P.either(
  LocationViewStateParser,
  () => DetailViewStateParser
);

// <NavigationStateFragment>: #/<formID>/<ViewState{1,}>/
const NavigationStateFragmentParser = pipe(
  S.string("#/"),
  P.chain(() => P.of<string, object>({})),
  P.bind("formId", () => FormIDParser),
  skip(() => SeperatorParser),
  P.bind("views", () => P.sepBy1(SeperatorParser, ViewStateParser)),
  skip(() => SeperatorParser),
  P.map(({ formId, views }) => NavigationPath(formId, views))
);
// <NavigationStateFragments>: <NavigationStateFragment{0,}>
const NavigationStateFragmentsParser = pipe(
  P.manyTill(NavigationStateFragmentParser, P.eof())
);

export class FragmentParser extends UGParser {
  static parseFragments(
    fragmentString: string
  ): Either<string, NavigationPath[]> {
    return pipe(
      fragmentString,
      FragmentParser.parse(NavigationStateFragmentsParser),
      either.map(readonlyArray.toArray)
    );
  }
}
